import React, { useEffect, useRef, useCallback, useState } from 'react';

import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import { useParams } from 'react-router-dom';

import * as Yup from 'yup';
import { FaSpinner } from 'react-icons/fa';
import Menu from '../../components/Menu';

import Input from '../../components/Input';
// 1. Importando o componente de imagem
import ImageInput from '../../components/ImageInput';
import Button from '../../components/Button';
import Textarea from '../../components/Textarea';

import Loading from '../../components/Loading';
import api from '../../services/api';

import { useToast } from '../../hooks/toast';

import { Container, Content, ContentContainer } from './styles';
import getValidationErrors from '../../utils/getValidationErrors';

// Interface atualizada
interface Article {
  title: string;
  authors: string;
  abstract: string;
  pdf_url: string;
  status: number;
  cover?: string; // ou 'avatar', dependendo de como o ImageInput espera receber a URL no initialData
}

const AlterarArtigo: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const { addToast } = useToast();
  const { id } = useParams<Record<string, string>>();

  // 2. Estado para armazenar os dados e preencher o initialData (necessário para o preview da imagem)
  const [article, setArticle] = useState<Article>();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    async function loadArticle(): Promise<void> {
      setIsLoading(true);
      try {
        const response = await api.get(`/articles/find/${id}`);

        // Armazena no estado para o initialData funcionar
        setArticle(response.data);
        formRef?.current?.setData(response.data);
      } catch (error) {
        addToast({
          type: 'error',
          title: 'Erro ao carregar artigo',
          description: 'Ocorreu um erro ao buscar as informações do artigo.',
        });
      } finally {
        setIsLoading(false);
      }
    }

    loadArticle();
  }, [id, addToast]);

  // 3. Função de troca de imagem (Baseada no AlterarCurso)
  const handleChangeCover = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    if (event.target.files && event.target.files[0]) {
      try {
        const data = new FormData();

        // Verifique se seu backend espera 'cover' ou 'avatar'
        data.append('cover', event.target.files[0]);

        // Chama a rota PATCH para atualizar apenas a imagem
        await api.patch(`/articles/cover/${id}`, data);

        addToast({
          type: 'success',
          title: 'Capa atualizada',
          description: 'A imagem de capa foi alterada com sucesso!',
        });

      } catch (e) {
        addToast({
          type: 'error',
          title: 'Erro na alteração da imagem',
          description: 'Ocorreu um erro ao alterar a capa do artigo.',
        });
      } finally {
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, [addToast, id]);

  const handleSubmit = useCallback(
    async (data: Article) => {
      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          title: Yup.string().required('Título do artigo é obrigatório'),
          authors: Yup.string().required('Autores são obrigatórios'),
          abstract: Yup.string().required('Resumo é obrigatório'),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        await api.put(`/articles/${id}`, data);

        addToast({
          type: 'success',
          title: 'Artigo Alterado',
          description: 'As informações do artigo foram alteradas com sucesso',
        });
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
        }

        addToast({
          type: 'error',
          title: 'Erro ao alterar artigo',
          description: 'Ocorreu um erro ao alterar as informações do artigo.',
        });
      }
    },
    [addToast, id],
  );

  return (
    <Container>
      <Menu />
      <Content>
        <ContentContainer>
          <h1>Alterar Artigo</h1>

          {/* Passamos 'article' no initialData para o ImageInput carregar a foto atual */}
          <Form initialData={article} ref={formRef} onSubmit={handleSubmit}>

            {/* 4. Componente ImageInput com o onChangeCapture */}
            <ImageInput name="cover" onChangeCapture={handleChangeCover} />

            <Input name="title" placeholder="Digite o título do artigo" label="Título" />
            <Input name="authors" placeholder="Digite os autores do artigo" label="Autores" />
            <Textarea name="abstract" label="Resumo" placeholder="Digite o resumo do artigo" />

            <Button type="submit" color="#2A2A29">Salvar Alterações</Button>
          </Form>
        </ContentContainer>

        {isLoading && (
          <div style={{ position: 'absolute', width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.2)', top: 0, left: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loading>
              <h1>Carregando</h1>
              <FaSpinner size={25} />
            </Loading>
          </div>
        )}
      </Content>
    </Container>
  );
};

export default AlterarArtigo;
